import { useState, useEffect, useMemo, useRef } from 'react'
import { Loader2, Zap, FlaskConical, Check } from 'lucide-react'
import { CopyButton } from '../styles'
import { proxyUrl, buildHeaders } from '@features/dashboards/testing-ground/utils'
import type { Creds, LogEntry } from '@features/dashboards/testing-ground/types'
import { ConfiguringChecklist } from './ConfiguringChecklist'
import { InteractionDiagram } from './InteractionDiagram'
import { LiveCredentialsPanel } from './LiveCredentialsPanel'
import { IdvConfigPanel } from './IdvConfigPanel'
import type { IdvDiagramConfig, IdvAiSuggestion } from './IdvConfigPanel'
import { IdvGuidedView } from './IdvGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { StageSaveBar } from './StageSaveBar'
import { CONFIG_CHECKLISTS } from '../data'
import { buildIdvDiagramDsl } from '../dynamicInteractionDiagrams'
import { IDV_FLOWS, DEFAULT_VENDOR_SETTINGS, DEFAULT_SMS_CONFIG } from '../idvConfigData'
import type { WorkflowDerivedConfig } from '../workflowDerivedConfig'
import type { PlatformJourneyRecord } from '@api/client'
import type { AnyObj } from '../types'
import type { AiConfigureSuggestions, AiStageSuggestionState } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'

interface IdvBiometricsStageProps {
  creds: Creds
  hasCreds: boolean
  onCredsChange: (c: Creds) => void
  /** True once customerId/childCustomerId come from an existing customer's own Scope data. */
  credsLocked: boolean
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
  entityId: string | null
  onEntityCreated: (entityId: string) => void
  onRunningChange: (running: boolean) => void
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  customerName: string | null
  platformVersion: 'v1' | 'v2'
  /** The workflow selected/opened in the Workflow Builder slate, if any. Real
   * workflow JSON doesn't carry an IDV vendor/flow field today, so this can only
   * confirm "a workflow already covers IDV" and freeze whatever flow is currently
   * active — it can't derive a specific vendor the way KYC + Cascade can. */
  builderConfig: WorkflowDerivedConfig | null
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell so Account Setup's checklist and this stage's
   * own banner share one apply/dismiss state instead of drifting apart. */
  idvAi: AiStageSuggestionState<IdvAiSuggestion>
}

const DEFAULT_DIAGRAM_CONFIG: IdvDiagramConfig = {
  defaultProvider: 'Daon',
  vendorSettings: DEFAULT_VENDOR_SETTINGS,
  smsConfig: DEFAULT_SMS_CONFIG,
  autoRunKyc: true,
  cleanupUrlsOnComplete: true,
}

const SIMULATED_STEPS = ['Document capture', 'Face match & liveness', 'Result']

export function SimulatedVerificationPanel({ eventsCount }: { eventsCount: number }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 gap-3">
      <FlaskConical size={22} className="text-[#a78bfa]" />
      <p className="text-[11px] font-semibold text-[#7c3aed]">Simulated verification session</p>
      <p className="text-[10px] text-[#94a3b8] text-center max-w-[260px]">No real FrankieOne call is made — this walks through the same steps to demo the flow.</p>
      <div className="w-full max-w-[240px] mt-2 space-y-2">
        {SIMULATED_STEPS.map((label, i) => {
          const done = eventsCount > i
          return (
            <div key={label} className="flex items-center gap-2 text-[11px]" style={{ color: done ? '#15803d' : '#94a3b8' }}>
              {done ? <Check size={12} className="shrink-0" /> : <Loader2 size={12} className="shrink-0 animate-spin" />}
              {label}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function IdvBiometricsStage({
  creds, hasCreds, onCredsChange, credsLocked, addLog, entityId, onEntityCreated, onRunningChange,
  customerMode, existingCustomer, customerName, platformVersion, builderConfig,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneySaved,
  aiSuggestions, idvAi,
}: IdvBiometricsStageProps) {
  const [flowId, setFlowId] = useState('idv_daon')
  const lockedByBuilder = !!builderConfig?.hasIdvStep
  const [diagramConfig, setDiagramConfig] = useState<IdvDiagramConfig>(DEFAULT_DIAGRAM_CONFIG)
  const [checklistEnabled, setChecklistEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_CHECKLISTS.idv.map((i) => [i.id, true]))
  )
  function toggleChecklistItem(id: string) {
    setChecklistEnabled((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // Hydrate from a saved journey once per distinct journey load — never
  // overwrites in-progress local edits with nulls from a brand-new journey.
  useEffect(() => {
    if (!journeyRecord) return
    if (journeyRecord.idvConfig) {
      const cfg = journeyRecord.idvConfig as { flowId?: string; diagramConfig?: IdvDiagramConfig }
      if (cfg.flowId) setFlowId(cfg.flowId)
      if (cfg.diagramConfig) setDiagramConfig(cfg.diagramConfig)
    }
    if (journeyRecord.idvChecklist) setChecklistEnabled(journeyRecord.idvChecklist as Record<string, boolean>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])
  const [loading, setLoading] = useState(false)
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; [k: string]: unknown }[]>([])
  const [livePhase, setLivePhase] = useState<'idle' | 'requesting' | 'awaiting-capture'>('idle')
  // True once a simulated run has produced the current entityId/events — lets the
  // "Hosted Verification" panel show a simulated placeholder instead of a real iframe
  // (there's no hostedUrl to embed) without being confused with the empty starting state.
  const [simulating, setSimulating] = useState(false)
  const [simulated, setSimulated] = useState(false)
  const simTimersRef = useRef<number[]>([])
  useEffect(() => () => { simTimersRef.current.forEach((id) => window.clearTimeout(id)) }, [])
  function simSleep(ms: number) {
    return new Promise<void>((resolve) => {
      simTimersRef.current.push(window.setTimeout(resolve, ms))
    })
  }

  // Falls back to a synthetic "Custom" entry (rather than IDV_FLOWS[0]) when the rep
  // has typed a real flow ID from the customer's own FrankieOne account — otherwise the
  // diagram would silently show "Default"/"default" while the live call actually sends
  // the real custom ID, misleading whoever's watching it.
  const activeFlow = IDV_FLOWS.find((f) => f.flowId === flowId)
    ?? { id: 'custom', name: 'Custom flow', type: '—', flowId }
  const diagramDsl = useMemo(
    () => buildIdvDiagramDsl({ platformVersion, flow: activeFlow, ...diagramConfig }),
    [platformVersion, activeFlow, diagramConfig],
  )

  // Drives the "How it works" diagram's live highlight. The hosted capture itself runs
  // inside an opaque iframe we can't see into — beyond the URL-generation call, the only
  // signal available is whatever postMessage `type` the hosted SDK happens to emit, so
  // matching further steps is best-effort keyword matching, not a guaranteed 1:1 mapping.
  const activeStepMatch = useMemo(() => {
    if (livePhase === 'requesting') return /Generate Hosted OneSDK URL|Generate hosted onboarding URL/i
    if (livePhase === 'awaiting-capture') {
      const lastEvent = events[events.length - 1]
      const t = lastEvent ? String(lastEvent.type ?? '').toLowerCase() : ''
      if (/complete|success|result|finish/.test(t)) return /Show verification result/i
      if (/face|liveness|biometric|selfie/.test(t)) return /face scan|liveness/i
      if (/document|ocr|capture/.test(t)) return /document capture|Photographs ID document/i
      return /Embed hosted URL in iframe/i
    }
    return null
  }, [livePhase, events])

  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.data?.['X-Frankie-Source'] === 'hosted-onesdk') {
        setEvents(prev => [...prev, { ts: new Date().toLocaleTimeString(), ...(e.data as Record<string, unknown>) }])
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  async function generateUrl() {
    setLoading(true)
    onRunningChange(true)
    setHostedUrl(null)
    setEvents([])
    setSimulated(false)
    setLivePhase('requesting')
    const payload = { consentObtained: true, oneSDKFlowId: flowId, redirectUrl: `${window.location.origin}/dashboard/implementations/platform-journey` }
    const url = `${creds.apiBase}/individuals/hostedUrl`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl('/individuals/hostedUrl'), { method: 'POST', headers: buildHeaders(creds, true), body: JSON.stringify(payload) })
      const durationMs = Date.now() - start
      const data = await res.json() as { entityId?: string; url?: string }
      addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: payload, responseBody: data, annotation: 'Generate Hosted OneSDK URL for IDV & biometrics capture' })
      if (res.ok && data?.url) {
        setHostedUrl(data.url)
        setLivePhase('awaiting-capture')
        if (data.entityId) onEntityCreated(data.entityId)
      } else {
        setLivePhase('idle')
      }
    } catch {
      setLivePhase('idle')
    } finally {
      setLoading(false)
      onRunningChange(false)
    }
  }

  // For customers with no account provisioned in FrankieOne yet — walks through the
  // same phases/events a real hosted session produces, purely client-side, so the
  // diagram lights up the same way without a real customerId/API key/entitlement.
  async function simulateCapture() {
    simTimersRef.current.forEach((id) => window.clearTimeout(id))
    simTimersRef.current = []
    setSimulating(true)
    setSimulated(true)
    onRunningChange(true)
    setHostedUrl(null)
    setEvents([])
    setLivePhase('requesting')
    await simSleep(900)
    setLivePhase('awaiting-capture')
    await simSleep(1400)
    setEvents((prev) => [...prev, { ts: new Date().toLocaleTimeString(), type: 'document:capture' }])
    await simSleep(1400)
    setEvents((prev) => [...prev, { ts: new Date().toLocaleTimeString(), type: 'liveness:check' }])
    await simSleep(1400)
    setEvents((prev) => [...prev, { ts: new Date().toLocaleTimeString(), type: 'idv:success' }])
    await simSleep(600)
    onEntityCreated(`SIM-${Math.random().toString(36).slice(2, 10).toUpperCase()}`)
    setSimulating(false)
    onRunningChange(false)
  }

  return (
    <div className="space-y-3">
      {customerMode === 'existing' ? (
        <div className="space-y-3">
          <ExistingCustomerPanel
            productKey="idv"
            sections={['ID Verification']}
            title="IDV & Biometrics — Existing Customer"
            description="Shows this customer's ID Verification & Biometrics settings as scoped in DealHub (document/biometric requirements, flow type). Reads DealHub's own presales record, not FrankieOne's live platform."
            showCascadeSummary={false}
            sharedSelection={existingCustomer}
            searchable={false}
          />
          {idvAi.visible && aiSuggestions?.idv && (
            <AiSuggestionBanner
              rationale={aiSuggestions.idv.rationale}
              confidence={aiSuggestions.idv.confidence}
              applied={idvAi.applied}
              onApply={idvAi.apply}
              onDismiss={idvAi.dismiss}
            />
          )}
          <IdvConfigPanel activeFlowId={flowId} onActiveFlowChange={setFlowId} onDiagramConfigChange={setDiagramConfig} aiSeed={idvAi.seed} />
        </div>
      ) : (
      <>
      <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />
      <StageSaveBar
        journeyId={journeyId}
        buildFields={() => ({ idvConfig: { flowId, diagramConfig }, idvChecklist: checklistEnabled })}
        onSaved={onJourneySaved}
      />
      <LiveCredentialsPanel creds={creds} onChange={onCredsChange} locked={credsLocked} />

      {experienceMode === 'guided' && (
        <IdvGuidedView
          activeFlow={activeFlow}
          hostedUrl={hostedUrl}
          loading={loading}
          hasCreds={hasCreds}
          entityId={entityId}
          events={events}
          customerName={customerName}
          onGenerateUrl={() => void generateUrl()}
          simulating={simulating}
          simulated={simulated}
          onSimulate={() => void simulateCapture()}
          checklistItems={CONFIG_CHECKLISTS.idv}
          checklistEnabled={checklistEnabled}
          onToggleChecklistItem={toggleChecklistItem}
          diagramDsl={diagramDsl}
          activeStepMatch={activeStepMatch}
          flowId={flowId}
          onActiveFlowChange={setFlowId}
          onDiagramConfigChange={setDiagramConfig}
        />
      )}

      {experienceMode === 'classic' && (
      <>
      <ConfiguringChecklist productLabel="IDV & Biometrics" items={CONFIG_CHECKLISTS.idv} enabled={checklistEnabled} onToggle={toggleChecklistItem} customerName={customerName} />
      {idvAi.visible && aiSuggestions?.idv && (
        <AiSuggestionBanner
          rationale={aiSuggestions.idv.rationale}
          confidence={aiSuggestions.idv.confidence}
          applied={idvAi.applied}
          onApply={idvAi.apply}
          onDismiss={idvAi.dismiss}
        />
      )}
      <IdvConfigPanel activeFlowId={flowId} onActiveFlowChange={setFlowId} onDiagramConfigChange={setDiagramConfig} lockedByBuilder={lockedByBuilder} aiSeed={idvAi.seed} />
      <InteractionDiagram stageId="idv" dsl={diagramDsl} activeStepMatch={activeStepMatch} />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(320px, 380px) 1fr' }}>
        <div className="rounded-[14px] overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
          <div className="px-4 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
            <p className="text-[12px] font-semibold text-[#1e293b]">IDV & Biometrics</p>
            <p className="text-[10px] text-[#64748b] mt-0.5">Document capture, face match, liveness — via Hosted OneSDK</p>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <span className="text-[10px] text-[#64748b]">Flow</span>
              <span className="text-[11px] font-mono text-[#1e293b] flex-1 truncate">
                {IDV_FLOWS.find((f) => f.flowId === flowId)?.name ?? flowId}
              </span>
              <span className="text-[9px] font-mono text-[#64748b]">{flowId}</span>
            </div>
            <button
              onClick={() => void generateUrl()}
              disabled={loading || simulating || !hasCreds}
              className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
            >
              {loading
                ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Generating…</span>
                : 'Start IDV Capture'}
            </button>
            {!hasCreds && <p className="text-[10px] text-amber-700 text-center">Configure credentials above to make live calls</p>}
            <button
              onClick={() => void simulateCapture()}
              disabled={loading || simulating}
              className="w-full py-2 rounded-[9px] text-[12px] font-semibold transition-all disabled:opacity-40 flex items-center justify-center gap-1.5"
              style={{ background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.35)', color: '#7c3aed' }}
            >
              {simulating
                ? <span className="flex items-center justify-center gap-2"><Loader2 size={13} className="animate-spin" />Simulating…</span>
                : <><FlaskConical size={13} /> Simulate Capture</>}
            </button>
            <p className="text-[10px] text-[#94a3b8] text-center -mt-1">No FrankieOne account needed — walks through the same steps client-side.</p>
            {entityId && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <span className="text-[10px] font-semibold text-emerald-700">{simulated ? 'entityId (simulated)' : 'entityId'}</span>
                <span className="text-[10px] font-mono text-[#475569] flex-1 truncate">{entityId}</span>
                <CopyButton text={entityId} />
              </div>
            )}
            {hostedUrl && (
              <a href={hostedUrl} target="_blank" rel="noreferrer" className="text-[10px] font-mono break-all hover:underline block" style={{ color: '#7c3aed' }}>
                ↗ Open in new tab
              </a>
            )}
            <p className="text-[10px] text-[#475569] italic pt-1">
              {simulated
                ? 'This was a simulated session — no real FrankieOne call was made, so nothing here reflects an actual verification outcome.'
                : 'The applicant completes document capture and a face scan inside the panel on the right — this is a real Hosted OneSDK session, so a camera is needed to finish it live.'}
            </p>
          </div>
        </div>

        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Hosted Verification</p>
            {events.length > 0 && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(167,139,250,.2)', color: '#7c3aed' }}>
                {events.length} event{events.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {hostedUrl ? (
            <iframe
              src={hostedUrl}
              allow="camera;fullscreen;accelerometer;gyroscope;magnetometer;geolocation;microphone;"
              className="w-full"
              style={{ minHeight: 560, border: 0 }}
              title="FrankieOne Hosted Verification"
            />
          ) : simulating || simulated ? (
            <SimulatedVerificationPanel eventsCount={events.length} />
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <Zap size={22} className="text-[#94a3b8] mb-3" />
              <p className="text-[11px] text-[#475569] italic">Click &quot;Start IDV Capture&quot; to load the verification panel</p>
            </div>
          )}
          {events.length > 0 && (
            <div style={{ maxHeight: 160, overflowY: 'auto', borderTop: '1px solid #e5e7eb' }}>
              {events.map((ev, i) => (
                <div key={i} className="px-4 py-2 border-b flex items-center gap-2" style={{ borderColor: '#e5e7eb' }}>
                  <span className="font-mono text-[9px] text-[#64748b]">{String(ev.ts)}</span>
                  {ev.type !== undefined && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-medium" style={{ background: 'rgba(167,139,250,.2)', color: '#7c3aed' }}>
                      {String((ev as AnyObj).type)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}
      </>
      )}
    </div>
  )
}
